import { useQueryClient } from "@tanstack/react-query";
import type AgentDomain from "entities/domain/agents/agent-domain";
import { SocketConversationUnreadCountChanged } from "entities/ISocketArgs";
import { UnreadCountDTO } from "entities/dto/UnreadCountDTO";
import { useWebSocket } from "hooks/use-socket";
import { useEffect } from "react";
import { canManageTeamInbox, canManageUnassignedInbox } from "util/permissions";
import { unreadCountsQueryKey, useCurrentAgent } from "./use-current-agent";

type SocketUnreadCount = SocketConversationUnreadCountChanged["unread_count"];

interface PermissionAdjustedCounts {
  shared: number;
  unassigned: number;
}

/**
 * Mirrors the API's unread_count_to_dto permission shaping. Socket events carry
 * the raw merchant projection, while the query cache carries the actor-visible
 * projection returned by HTTP; replacing one with the other without this step
 * makes badges widen after the first realtime update.
 */
function calculatePermissionAdjustedCounts(
  manageTeam: boolean,
  manageUnassigned: boolean,
  personal: number,
  shared: number,
  unassigned: number
): PermissionAdjustedCounts {
  if (!manageTeam && !manageUnassigned) {
    return { shared: personal, unassigned: 0 };
  }

  if (manageTeam && !manageUnassigned) {
    return { shared: Math.max(shared - unassigned, 0), unassigned: 0 };
  }

  if (!manageTeam && manageUnassigned) {
    return { shared: personal + unassigned, unassigned };
  }

  return { shared, unassigned };
}

/**
 * The socket payload carries per-agent maps (personal counts keyed by agent id);
 * the cache holds a single flattened UnreadCountDTO for the current agent. This
 * collapses the payload down to that shape.
 */
function buildUnreadCountForCurrentAgent(
  unreadCountChanges: SocketUnreadCount,
  currentAgent: AgentDomain
): UnreadCountDTO {
  const agentKey = currentAgent.id.toString();
  const manageTeam = canManageTeamInbox(
    unreadCountChanges.merchant_id,
    currentAgent
  );
  const manageUnassigned = canManageUnassignedInbox(
    unreadCountChanges.merchant_id,
    currentAgent
  );
  const personal = unreadCountChanges.personal[agentKey] || 0;
  const adjusted = calculatePermissionAdjustedCounts(
    manageTeam,
    manageUnassigned,
    personal,
    unreadCountChanges.shared,
    unreadCountChanges.unassigned
  );

  return {
    merchant_id: unreadCountChanges.merchant_id,
    shared: adjusted.shared,
    personal,
    unassigned: adjusted.unassigned,
    inboxes:
      unreadCountChanges.inboxes &&
      Object.keys(unreadCountChanges.inboxes).reduce(
        (changedInboxes, currentInboxId) => {
          const inboxUnreadCount = unreadCountChanges.inboxes![currentInboxId];
          const inboxPersonal = inboxUnreadCount.personal[agentKey] || 0;
          const inboxAdjusted = calculatePermissionAdjustedCounts(
            manageTeam,
            manageUnassigned,
            inboxPersonal,
            inboxUnreadCount.shared,
            inboxUnreadCount.unassigned
          );

          changedInboxes[currentInboxId] = {
            shared: inboxAdjusted.shared,
            personal: inboxPersonal,
            unassigned: inboxAdjusted.unassigned,
          };

          return changedInboxes;
        },
        {} as NonNullable<UnreadCountDTO["inboxes"]>
      ),
  };
}

/**
 * Subscribes to `conversation_unread_count_changed` socket events and writes the
 * result straight into the unread-counts query cache.
 *
 * Writing to the cache directly (rather than routing through React state in the
 * authenticated page wrapper) keeps these high-frequency events from re-rendering
 * the whole app shell — only components that read the unread-counts query update.
 * The functional updater also merges against the latest cache, so bursts of
 * events can't clobber each other via a stale closure.
 */
export function useUnreadCountsRealtimeUpdates() {
  const queryClient = useQueryClient();
  const { data: currentAgent } = useCurrentAgent();
  const { addEventHandler, removeEventHandler } = useWebSocket();

  useEffect(() => {
    const handleConversationUnreadCountChanged = (
      args: SocketConversationUnreadCountChanged
    ) => {
      if (!currentAgent) return;

      const merchantIdsAgentBelongsTo = currentAgent.merchants.map((m) => m.id);
      if (!merchantIdsAgentBelongsTo.includes(args.unread_count.merchant_id)) {
        return;
      }

      const nextUnreadCount = buildUnreadCountForCurrentAgent(
        args.unread_count,
        currentAgent
      );

      queryClient.setQueryData<UnreadCountDTO[]>(
        unreadCountsQueryKey(),
        (old = []) => [
          ...old.filter((uc) => uc.merchant_id !== nextUnreadCount.merchant_id),
          nextUnreadCount,
        ]
      );
    };

    addEventHandler(
      "conversation_unread_count_changed",
      handleConversationUnreadCountChanged
    );

    return () => {
      removeEventHandler(
        "conversation_unread_count_changed",
        handleConversationUnreadCountChanged
      );
    };
  }, [queryClient, currentAgent, addEventHandler, removeEventHandler]);
}
